import type { InventoryCount, InventoryGroup, InventorySection, InventorySectionProduct, CountReviewBelowMinProduct } from '../../services/inventory/inventoryService';
import type { ExpectedInventorySettings, ExpectedInventoryMinimum } from '../../services/inventory/expectedInventoryService';
import type { OrderHistory } from '../../stores/appOrderHistoryStore';
import type { RevenueHistory } from '../../services/revenuehistory/revenueHistoryService';
import { EstimatedInventoryInputs, EstimatedProductRow, EstimatedGroup, CountedProductAgg, sumLatestCounts } from './estimatedInventory';
import { collectWindowPurchases, inventoryKey, purchasedQtyForCount, neededQtyForCount, countSpaceBridge, frozenCountSpace } from './consumptionReport';
import { buildNeededIndex, collectNeedProductInfo } from './utilizationReport';
import { applySubrecipeExplosion, openingStockFitsSupply } from './inventoryVariance';
import { baseProductKey } from '../inventoryProductKey';
import { selectValuationLine } from '../platformUtils';
import { pricePerStdUnit } from './shared';
import { explodeSubrecipeToProductNeeds } from './recipeExplosion';

export interface ExpectedProductRow extends EstimatedProductRow {
  baselineDates: string[];
  baselineKeys: string[];
  combinedExpectedQty: number | null;
  otherGroupIds: string[];
  notCounted: boolean;
  estimatedAllocation: boolean;
  belowMinimum: boolean;
  /** Assignment display metadata (first assignment of this product in its groups). */
  inventoryCategoryName: string | null;
  inventoryCategoryPosition: number | null;
  supplier: { platform: string | null; name: string | null };
  /** Real minimum in its own count unit; null when none is set (legacy fallback rows included). */
  minimum: { qty: number; unit: string } | null;
  /** Suggested order in count units when below a real minimum. */
  toOrder: { qty: number; unit: string } | null;
}
export interface ExpectedOrderProduct extends CountReviewBelowMinProduct { otherGroupIds: string[] }
export interface ExpectedGroup extends EstimatedGroup {
  products: ExpectedProductRow[];
  baselineDates: string[];
  baselineKeys: string[];
  belowMinProducts: ExpectedOrderProduct[];
}
export interface ExpectedOverlapSetting {
  groupId: string;
  fullCountGroupId: string;
  sharedProductCount: number;
  mode: 'included' | 'extra';
  automatic: boolean;
}
export interface ExpectedCountDayOrders {
  key: string;
  groupId: string;
  sessionKey: string;
  countDate: string;
  countedAt: number;
  orders: OrderHistory[];
  countedOrderIds: string[];
  automatic: boolean;
}
export interface ExpectedInventoryData {
  hasData: boolean;
  groups: ExpectedGroup[];
  overlapSettings: ExpectedOverlapSetting[];
  countDayOrders: ExpectedCountDayOrders[];
  orderProducts: ExpectedOrderProduct[];
}
export interface ExpectedInventoryInputs extends EstimatedInventoryInputs {
  groups: InventoryGroup[];
  sections: InventorySection[];
  sectionProducts: InventorySectionProduct[];
  minimums: ExpectedInventoryMinimum[];
  settings: ExpectedInventorySettings;
}

const clock = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Amsterdam', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23',
});
/** Business dates use Amsterdam calendar days, including midnight/noon adapter anchors. */
export function expectedInventoryDate(value: Date | string | number): string {
  const parts = clock.formatToParts(new Date(value));
  const read = (type: string) => parts.find(part => part.type === type)?.value ?? '';
  return `${read('year')}-${read('month')}-${read('day')}`;
}
function hourAt(value: number): number {
  return Number(clock.formatToParts(new Date(value)).find(part => part.type === 'hour')?.value);
}
export function expectedUsageFraction(countedAt: number): number {
  const hour = hourAt(countedAt);
  return hour < 16 ? 1 : hour < 22 ? 0.7 : 0;
}

/** A saved count session of one group (sections folded together). Shared with the dashboard's stock-adjusted cost %. */
export interface ExpectedSession {
  key: string;
  groupId: string;
  at: number;
  day: string;
  counts: InventoryCount[];
  products: Map<string, CountedProductAgg>;
}
type Session = ExpectedSession;
interface Observation { session: Session; product: CountedProductAgg; notCounted: boolean; frozenPrice: number | null }
function space(product: CountedProductAgg) {
  return { stdUnit: product.stdUnit, countUnit: product.countUnit, stdPerCountUnit: product.countFactor };
}
function emptyProduct(name: string, stdUnit: string, countUnit = '', countFactor: number | null = null): CountedProductAgg {
  return { name, stdUnit, countUnit, countFactor, totalQty: 0, value: 0, unitMismatch: false, qtyUnreadable: false };
}
export interface SavedSessionInputs {
  countsHistory: InventoryCount[];
  sections: InventorySection[];
  groups: InventoryGroup[];
  currentRestaurantId: string;
  now: number;
}
/** Neutral saved-session grouping: frozen products per (group, session), newest first. No subrecipe explosion. */
export function groupSavedSessions(inputs: SavedSessionInputs): Session[] {
  const sectionGroups = new Map(inputs.sections.map(section => [section.id, section.groupId]));
  const groups = new Set(inputs.groups.map(group => group.id));
  const buckets = new Map<string, Session>();
  for (const count of inputs.countsHistory) {
    if (count.restaurantId !== inputs.currentRestaurantId) continue;
    const groupId = count.groupId ?? sectionGroups.get(count.inventorySectionId ?? '');
    const at = new Date(count.countedAt).getTime();
    if (!groupId || !groups.has(groupId) || !Number.isFinite(at) || at > inputs.now) continue;
    const day = expectedInventoryDate(at);
    const key = count.sessionId || `legacy:${day}`;
    const id = `${groupId}|${key}`;
    let session = buckets.get(id);
    if (!session) {
      session = { key, groupId, at, day, counts: [], products: new Map() };
      buckets.set(id, session);
    }
    session.counts.push(count);
    if (at > session.at) { session.at = at; session.day = day; }
  }
  for (const session of Array.from(buckets.values())) {
    session.counts.sort((a, b) => new Date(b.countedAt).getTime() - new Date(a.countedAt).getTime() || a.id.localeCompare(b.id));
    session.products = sumLatestCounts(session.counts);
  }
  return Array.from(buckets.values()).sort((a, b) => b.at - a.at || a.key.localeCompare(b.key));
}
function sessionsFor(inputs: ExpectedInventoryInputs): Session[] {
  const sessions = groupSavedSessions(inputs);
  for (const session of sessions) {
    // The shared explosion omits incompatible leaves. Expected stock must not turn
    // that partial sum into a known quantity (or a fabricated order shortfall).
    const unreadableLeaves = new Map<string, { name: string; stdUnit: string }>();
    session.products.forEach((product, key) => {
      if (!key.startsWith('subrecipe:') || (!product.totalQty && !product.qtyUnreadable)) return;
      const recipe = inputs.subrecipesById.get(key.slice('subrecipe:'.length));
      if (!recipe) return;
      const needs = explodeSubrecipeToProductNeeds(recipe, inputs.subrecipesById, {
        defaultWastePercentage: inputs.defaultWastePercentage, restaurantId: inputs.currentRestaurantId, keyOf: inventoryKey,
      });
      needs.forEach(need => {
        const loose = session.products.get(need.productKey);
        if (product.qtyUnreadable || (loose && loose.stdUnit !== need.stdUnit)) {
          unreadableLeaves.set(need.productKey, { name: need.productName, stdUnit: need.stdUnit });
        }
      });
    });
    applySubrecipeExplosion(session.products, inputs, identity => emptyProduct(identity.name, identity.stdUnit));
    unreadableLeaves.forEach((identity, key) => {
      const product = session.products.get(key) ?? emptyProduct(identity.name, identity.stdUnit);
      product.qtyUnreadable = true; session.products.set(key, product);
    });
  }
  return sessions;
}

/** Clone just the sales quantities the shared recipe engine consumes, including BYO choices. */
export function expectedWindowRevenue(revenue: RevenueHistory[], at: number, now: number): RevenueHistory[] {
  return expectedIntervalRevenue(revenue, at, undefined, now);
}

/** Share of business day `day`'s sales that fall after moment `at` (count-time rule above). */
function salesRemainingAfter(at: number, day: string): number {
  const atDay = expectedInventoryDate(at);
  return day > atDay ? 1 : day < atDay ? 0 : expectedUsageFraction(at);
}
/** Share of business day `day`'s sales between moments `start` and `end` (end omitted = open). */
export function expectedDayWeight(start: number, end: number | undefined, day: string): number {
  return Math.max(0, salesRemainingAfter(start, day) - (end === undefined ? 0 : salesRemainingAfter(end, day)));
}

/** Disjoint portions of business-day sales; never subtract differently allocated recipe needs. */
function expectedIntervalRevenue(revenue: RevenueHistory[], at: number, end: number | undefined, now: number): RevenueHistory[] {
  const first = expectedInventoryDate(at);
  const today = expectedInventoryDate(now);
  const last = end === undefined ? undefined : expectedInventoryDate(end);
  return revenue.flatMap(row => {
    if (!Number.isFinite(new Date(row.date).getTime())) return [];
    const day = expectedInventoryDate(row.date);
    if (day < first || day >= today) return [];
    if (last && day > last) return [];
    const weight = expectedDayWeight(at, end, day);
    if (!weight) return [];
    if (weight === 1) return [row];
    return [{ ...row, revenueData: { ...row.revenueData, categories: row.revenueData.categories.map(category => ({
      ...category, quantity: category.quantity * weight, items: category.items.map(item => ({
        ...item, quantity: item.quantity * weight,
        ...(item.choices ? { choices: item.choices.map(choice => ({ ...choice, quantity: choice.quantity * weight })) } : {}),
      })),
    })) } }];
  });
}

export function resolveExpectedCountDayOrders(
  session: { key: string; groupId: string; at: number; day: string }, orders: OrderHistory[], settings: ExpectedInventorySettings,
): ExpectedCountDayOrders {
  const eligible = orders.filter(order => Number.isFinite(new Date(order.orderDate).getTime()) && expectedInventoryDate(order.orderDate) === session.day);
  const ids = new Set(eligible.map(order => order.id));
  const key = `${session.groupId}|${session.key}`;
  const saved = settings.countDayOrders?.[key];
  const valid = saved && saved.countDate === session.day && saved.countedOrderIds.every(id => ids.has(id));
  return {
    key, groupId: session.groupId, sessionKey: session.key, countDate: session.day, countedAt: session.at, orders: eligible,
    countedOrderIds: valid ? [...saved.countedOrderIds] : hourAt(session.at) < 12 ? [] : eligible.map(order => order.id),
    automatic: !valid,
  };
}

export function overlapsFor(inputs: Pick<ExpectedInventoryInputs, 'groups' | 'settings'>, configured: Map<string, Set<string>>, sessions: Session[]): ExpectedOverlapSetting[] {
  const groups = [...inputs.groups].sort((a, b) => (configured.get(a.id)?.size ?? 0) - (configured.get(b.id)?.size ?? 0) || a.id.localeCompare(b.id));
  const result: ExpectedOverlapSetting[] = [];
  groups.forEach((group, index) => {
    const keys = configured.get(group.id) ?? new Set<string>();
    if (!keys.size) return;
    const candidates = groups.slice(index + 1).map(parent => ({
      parent, shared: Array.from(keys).filter(key => configured.get(parent.id)?.has(key)),
    })).filter(candidate => candidate.shared.length).sort((a, b) => b.shared.length - a.shared.length ||
      (configured.get(b.parent.id)?.size ?? 0) - (configured.get(a.parent.id)?.size ?? 0) || a.parent.id.localeCompare(b.parent.id));
    const saved = inputs.settings.sameStock?.[group.id];
    const chosen = candidates.find(candidate => candidate.parent.id === saved?.fullCountGroupId) ?? candidates[0];
    if (!chosen) return;
    let mode: 'included' | 'extra' = 'extra';
    if (chosen.shared.length / keys.size >= 0.9) {
      let closest: { left: Session; right: Session; distance: number } | undefined;
      for (const left of sessions.filter(session => session.groupId === group.id)) {
        for (const right of sessions.filter(session => session.groupId === chosen.parent.id)) {
          const distance = Math.abs(left.at - right.at);
          if (distance <= 7 * 86400000 && (!closest || distance < closest.distance)) closest = { left, right, distance };
        }
      }
      if (!closest) mode = 'included';
      else {
        const value = (session: Session) => chosen.shared.reduce((sum, key) => sum + (session.products.get(key)?.value ?? 0), 0);
        const left = value(closest.left), right = value(closest.right);
        const largest = Math.max(Math.abs(left), Math.abs(right));
        if (!largest || Math.abs(left - right) / largest <= 0.3) mode = 'included';
      }
    }
    const override = saved?.fullCountGroupId === chosen.parent.id;
    result.push({ groupId: group.id, fullCountGroupId: chosen.parent.id, sharedProductCount: chosen.shared.length, mode: override ? saved.mode : mode, automatic: !override });
  });
  return result;
}

/** Expected-only projection. Existing leakage/report windows and frozen counts are untouched. */
export function computeExpectedInventory(inputs: ExpectedInventoryInputs): ExpectedInventoryData {
  const sessions = sessionsFor(inputs);
  const latest = new Map<string, Session>();
  sessions.forEach(session => { if (!latest.has(session.groupId)) latest.set(session.groupId, session); });
  const sectionGroups = new Map(inputs.sections.map(section => [section.id, section.groupId]));
  const configs = new Map<string, Map<string, InventorySectionProduct>>();
  const configured = new Map<string, Set<string>>();
  for (const product of inputs.sectionProducts) {
    if (product.restaurantId !== inputs.currentRestaurantId) continue;
    const groupId = sectionGroups.get(product.inventorySectionId);
    if (!groupId) continue;
    const key = baseProductKey(product.productKey);
    const map = configs.get(groupId) ?? new Map<string, InventorySectionProduct>();
    if (!map.has(key)) map.set(key, product);
    configs.set(groupId, map);
    if (!key.startsWith('subrecipe:')) {
      const keys = configured.get(groupId) ?? new Set<string>();
      keys.add(key); configured.set(groupId, keys);
    }
  }
  const overlapSettings = overlapsFor(inputs, configured, sessions);
  const orders = inputs.orders.filter(order => (!order.restaurantId || order.restaurantId === inputs.currentRestaurantId) &&
    Number.isFinite(new Date(order.orderDate).getTime()) && expectedInventoryDate(order.orderDate) <= expectedInventoryDate(inputs.now));
  const sortedOrders = [...orders].sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());
  const valuationLines = new Map<string, OrderHistory['productData']>();
  sortedOrders.forEach(order => order.productData.forEach(line => {
    const key = inventoryKey(order.platform, line.articleId, line.name);
    const candidates = valuationLines.get(key) ?? [];
    candidates.push(line); valuationLines.set(key, candidates);
  }));
  const revenue = inputs.revenue.filter(row => !row.restaurantId || row.restaurantId === inputs.currentRestaurantId);
  const selections = new Map<string, ExpectedCountDayOrders>();
  const selectionFor = (session: Session) => {
    const key = `${session.groupId}|${session.key}`;
    let selection = selections.get(key);
    if (!selection) { selection = resolveExpectedCountDayOrders(session, orders, inputs.settings); selections.set(key, selection); }
    return selection;
  };
  const needInfo = collectNeedProductInfo(inputs.costPriceList, inputs.subrecipesById, inventoryKey);
  function observation(session: Session, key: string, config?: InventorySectionProduct): Observation {
    const product = session.products.get(key);
    const price = product ? session.counts.flatMap(count => count.items).flatMap(item => {
      if (baseProductKey(item.productKey) !== key || !item.pricePerStdUnit) return [];
      const bridge = countSpaceBridge(frozenCountSpace(item), space(product));
      return bridge === null ? [] : [item.pricePerStdUnit / bridge];
    })[0] : undefined;
    return {
      session, product: product ? { ...product, countFactor: product.countFactor ??
        (config?.stdUnit === product.stdUnit && config.countUnit === product.countUnit ? config.countUnitToStdFactor : null) }
        : emptyProduct(config?.productName ?? key, config?.stdUnit ?? '', config?.countUnit ?? '', config?.countUnitToStdFactor ?? null),
      notCounted: !product, frozenPrice: price ?? (product?.totalQty && !product.qtyUnreadable ? product.value / product.totalQty || null : null),
    };
  }
  const keysByGroup = new Map<string, Set<string>>();
  latest.forEach((session, groupId) => {
    keysByGroup.set(groupId, new Set([...Array.from(configured.get(groupId) ?? []), ...Array.from(session.products.keys())]));
    selectionFor(session);
  });
  const allKeys = new Set(Array.from(keysByGroup.values()).flatMap(keys => Array.from(keys)));
  interface Projection {
    key: string;
    groupIds: string[];
    piles: Observation[];
    target: CountedProductAgg;
    starts: number[];
    allocatedBuys: number[];
    allocatedNeeds: number[];
    unreadable: boolean;
    unmeasurablePurchases: boolean;
    neededUnitMismatch: boolean;
    dishes: Set<string>;
  }
  const projections: Projection[] = [];
  for (const key of Array.from(allKeys)) {
    const groupIds = Array.from(keysByGroup).filter(([, keys]) => keys.has(key)).map(([id]) => id).sort();
    // Only explicit product assignment overlap participates; prep-only overlap is outside v1.
    const parent = new Map(groupIds.map(id => [id, id]));
    const find = (id: string): string => { const next = parent.get(id)!; return next === id ? id : find(next); };
    for (const relation of overlapSettings) {
      if (relation.mode !== 'included' || !parent.has(relation.groupId) || !parent.has(relation.fullCountGroupId) ||
        !configured.get(relation.groupId)?.has(key) || !configured.get(relation.fullCountGroupId)?.has(key)) continue;
      parent.set(find(relation.groupId), find(relation.fullCountGroupId));
    }
    const components = new Map<string, string[]>();
    groupIds.forEach(id => { const root = find(id); components.set(root, [...(components.get(root) ?? []), id]); });
    const piles: Observation[] = [];
    for (const ids of Array.from(components.values())) {
      // Included recounts can omit an observed product: absence must not supersede it as zero.
      const observed = ids.length > 1 ? sessions.find(session => ids.includes(session.groupId) && session.products.has(key)) : undefined;
      const chosen = observed ?? ids.map(id => latest.get(id)!).sort((a, b) => b.at - a.at || a.groupId.localeCompare(b.groupId))[0];
      piles.push(observation(chosen, key, configs.get(chosen.groupId)?.get(key) ?? configs.get(ids[0])?.get(key)));
    }
    piles.sort((a, b) => a.session.at - b.session.at || a.session.groupId.localeCompare(b.session.groupId));
    const target = piles[piles.length - 1].product;
    const conversions = piles.map(pile => countSpaceBridge(space(pile.product), space(target)));
    projections.push({ key, groupIds, piles, target,
      starts: piles.map((pile, index) => pile.product.totalQty * (conversions[index] ?? 0)),
      allocatedBuys: piles.map(() => 0), allocatedNeeds: piles.map(() => 0),
      unreadable: piles.some((pile, index) => pile.product.qtyUnreadable || conversions[index] === null),
      unmeasurablePurchases: false, neededUnitMismatch: false, dishes: new Set(),
    });
    piles.forEach(pile => selectionFor(pile.session));
  }
  const boundaries = Array.from(new Set(projections.flatMap(product => product.piles.map(pile => pile.session.at)))).sort((a, b) => a - b);
  const byKey = new Map(projections.map(product => [product.key, product]));
  const periodOrders = boundaries.map(() => [] as OrderHistory[]);
  const firstSession = projections.flatMap(product => product.piles.map(pile => pile.session)).sort((a, b) => a.at - b.at || a.groupId.localeCompare(b.groupId))[0];
  // An order line belongs to at most one interval for its product. Already counted
  // in any contributing pile takes priority over allocating purchases to older stock.
  for (const order of orders) {
    const day = expectedInventoryDate(order.orderDate);
    const linesByPeriod = new Map<number, OrderHistory['productData']>();
    for (const line of order.productData) {
      const product = byKey.get(inventoryKey(order.platform, line.articleId, line.name));
      const first = product?.piles[0].session ?? firstSession;
      if (!first) continue;
      const baselines = product ? product.piles.map(pile => pile.session) : [first];
      if (day < first.day || baselines.some(session => session.day === day && selectionFor(session).countedOrderIds.includes(order.id))) continue;
      let boundary = first.at;
      for (const pile of product?.piles ?? []) {
        if (pile.session.day <= day) {
          boundary = pile.session.at;
        }
      }
      // Boundaries of other products also matter: alternatives share one stock supply.
      // Date-only purchases after a count day land in the last segment preceding that day.
      let index = boundaries.indexOf(boundary);
      for (let next = index + 1; next < boundaries.length; next++) {
        if (expectedInventoryDate(boundaries[next]) >= day) break;
        index = next;
      }
      const lines = linesByPeriod.get(index) ?? [];
      lines.push(line); linesByPeriod.set(index, lines);
    }
    linesByPeriod.forEach((productData, index) => periodOrders[index].push({ ...order, productData }));
  }
  // Purchased alternatives outside the counted product list still carry supply. Keep
  // their unused purchases across intervals without presenting them as counted stock.
  const uncountedSupply = new Map<string, number>();
  boundaries.forEach((at, periodIndex) => {
    const end = boundaries[periodIndex + 1];
    const buys = collectWindowPurchases(periodOrders[periodIndex], -Infinity, Infinity);
    const supply = new Map(uncountedSupply);
    buys.forEach((buy, key) => {
      if (byKey.has(key)) return;
      const info = needInfo.get(key);
      if (!info || !info.stdUnit || buy.stdUnit === info.stdUnit) {
        supply.set(key, Math.max(0, (supply.get(key) ?? 0) + buy.baseQty));
      }
    });
    const active = projections.filter(product => product.piles[0].session.at <= at);
    for (const product of active) {
      if (product.unreadable) continue;
      const buy = buys.get(product.key);
      const purchase = purchasedQtyForCount(buy, product.target.stdUnit);
      const recipeUnit = neededQtyForCount(1, needInfo.get(product.key), product.target.stdUnit);
      // Recipe quantity and stock must enter the alternatives pool in the same space.
      // A measured-to-piece recipe bridge is reversible; an unknown bridge lends no stock.
      if (purchase.unitMismatch || (recipeUnit.unitMismatch && !recipeUnit.factor)) continue;
      const opening = product.piles.reduce((sum, pile, index) => pile.session.at <= at
        ? sum + product.starts[index] + product.allocatedBuys[index] - product.allocatedNeeds[index] : sum, 0);
      if (recipeUnit.qty && (needInfo.has(product.key) || openingStockFitsSupply(product.target.stdUnit, buy))) {
        supply.set(product.key, Math.max(0, opening + purchase.qty) / recipeUnit.qty);
      }
    }
    const segmentRevenue = expectedIntervalRevenue(revenue, at, end, inputs.now);
    const needed = buildNeededIndex(inputs.costPriceList, segmentRevenue, inputs.subrecipesById,
      inputs.defaultWastePercentage, inputs.currentRestaurantId, inventoryKey, supply);
    supply.forEach((qty, key) => {
      if (!byKey.has(key)) uncountedSupply.set(key, Math.max(0, qty - (needed.get(key)?.base ?? 0)));
    });
    for (const product of active) {
      const purchase = purchasedQtyForCount(buys.get(product.key), product.target.stdUnit);
      const usage = neededQtyForCount(needed.get(product.key)?.base ?? 0, needInfo.get(product.key), product.target.stdUnit);
      const badPurchase = purchase.unitMismatch || (!!buys.get(product.key)?.hadUnmeasurable && product.target.stdUnit !== 'unit');
      product.unmeasurablePurchases ||= badPurchase;
      product.neededUnitMismatch ||= usage.unitMismatch;
      product.unreadable ||= badPurchase || (usage.unitMismatch && !usage.factor);
      needed.get(product.key)?.dishes.forEach(dish => product.dishes.add(dish));
      const indices = product.piles.map((pile, index) => pile.session.at <= at ? index : -1).filter(index => index >= 0);
      const countedTotal = indices.reduce((sum, index) => sum + Math.max(0, product.starts[index]), 0);
      indices.forEach(index => {
        const share = countedTotal ? Math.max(0, product.starts[index]) / countedTotal : 1 / indices.length;
        product.allocatedBuys[index] += purchase.qty * share;
        product.allocatedNeeds[index] += usage.qty * share;
      });
    }
  });
  const rows = new Map<string, Map<string, ExpectedProductRow>>();
  const orderProducts: ExpectedOrderProduct[] = [];
  const assignmentGroups = new Map(inputs.sections.map(section => [section.id, section.groupId]));
  const assignmentFor = (key: string, groupIds: string[]) => inputs.sectionProducts.find(product =>
    baseProductKey(product.productKey) === key && groupIds.includes(assignmentGroups.get(product.inventorySectionId) ?? ''));
  for (const projection of projections) {
    const { key, groupIds, piles, target, starts, allocatedBuys, allocatedNeeds, unreadable } = projection;
    const startQty = starts.reduce((sum, qty) => sum + qty, 0);
    const purchasedQty = allocatedBuys.reduce((sum, qty) => sum + qty, 0);
    const neededQty = allocatedNeeds.reduce((sum, qty) => sum + qty, 0);
    const estimatedQty = startQty + purchasedQty - neededQty;
    const latestFrozen = [...piles].reverse().find(pile => pile.frozenPrice !== null);
    const fallbackConversion = latestFrozen ? countSpaceBridge(space(latestFrozen.product), space(target)) : null;
    // The order helper prices measured base units. A contradictory live unit cannot price the count.
    const priceLine = selectValuationLine((valuationLines.get(key) ?? []).filter(line =>
      pricePerStdUnit(line) !== null || target.stdUnit === 'unit'), line => line.price);
    let livePrice: number | null = null;
    if (priceLine) {
      const info = priceLine.quantityInfo;
      if (target.stdUnit === 'unit') livePrice = Math.abs(priceLine.price);
      else if (info?.stdUnit === target.stdUnit) livePrice = pricePerStdUnit(priceLine);
      else if (target.stdUnit === 'p' && info?.stdMultiplier && (info.stdUnit === 'g' || info.stdUnit === 'ml')) {
        // Same measured-to-piece bridge as recipe usage: stdAmount per piece,
        // stdMultiplier pieces per ordered package. Never multiply €/ml by bottles.
        livePrice = Math.abs(priceLine.price) / info.stdMultiplier;
      }
    }
    const price = livePrice ?? (latestFrozen && fallbackConversion ? latestFrozen.frozenPrice! / fallbackConversion : null);
    const baselineDates = Array.from(new Set(piles.map(pile => pile.session.day))).sort();
    const baselineKeys = Array.from(new Set(piles.map(pile => `${pile.session.groupId}|${pile.session.key}`)));
    const product: ExpectedProductRow = {
      key, productName: target.name, stdUnit: target.stdUnit,
      startQty: unreadable ? null : startQty, purchasedQty: unreadable ? null : purchasedQty, neededQty: unreadable ? null : neededQty,
      estimatedQty: unreadable ? null : estimatedQty, combinedExpectedQty: unreadable ? null : estimatedQty,
      countUnit: target.countUnit, estimatedCountQty: unreadable || !target.countFactor ? null : estimatedQty / target.countFactor,
      dishes: Array.from(projection.dishes).sort(),
      pricePerStdUnit: price, estimatedValue: unreadable || price === null ? null : estimatedQty * price,
      negative: !unreadable && estimatedQty < 0, hasUnmeasurablePurchases: projection.unmeasurablePurchases,
      noRecipeCoverage: neededQty === 0 && !projection.neededUnitMismatch,
      neededUnitMismatch: projection.neededUnitMismatch, countUnitMismatch: piles.some(pile => pile.product.unitMismatch),
      countQtyUnreadable: unreadable, onlyFromSubrecipe: piles.every(pile => !!pile.product.onlyFromSubrecipe),
      baselineDates, baselineKeys, otherGroupIds: [], notCounted: piles.some(pile => pile.notCounted), estimatedAllocation: piles.length > 1, belowMinimum: false,
      inventoryCategoryName: null, inventoryCategoryPosition: null, supplier: { platform: null, name: null }, minimum: null, toOrder: null,
    };
    const assignment = assignmentFor(key, groupIds);
    product.inventoryCategoryName = assignment?.inventoryCategoryName ?? null;
    product.inventoryCategoryPosition = assignment?.inventoryCategoryPosition ?? null;
    product.supplier = { platform: assignment?.platform ?? null, name: null };
    const thresholds = inputs.minimums.filter(minimum => baseProductKey(minimum.productKey) === key && groupIds.includes(minimum.groupId));
    // Real restaurant-wide minimum takes precedence over a different group's legacy fallback.
    const minimum = thresholds.find(threshold => threshold.minimum !== null) ?? thresholds[0];
    if (minimum) {
      product.supplier = minimum.supplier;
      if (minimum.minimum !== null) product.minimum = { qty: minimum.minimum, unit: minimum.countUnit };
    }
    if (minimum && !unreadable) {
      const factor = countSpaceBridge(space(target), { stdUnit: minimum.stdUnit, countUnit: minimum.countUnit, stdPerCountUnit: minimum.countUnitToStdFactor });
      if (factor !== null && estimatedQty * factor < minimum.thresholdInStdUnit) {
        product.belowMinimum = true;
        const current = estimatedQty * factor;
        const shortfall = minimum.thresholdInStdUnit - current;
        orderProducts.push({ productKey: key, productName: product.productName, groupId: minimum.groupId,
          groupName: inputs.groups.find(group => group.id === minimum.groupId)?.name ?? null, sections: [],
          currentCount: current / minimum.countUnitToStdFactor, countUnit: minimum.countUnit, currentInStdUnit: current, stdUnit: minimum.stdUnit,
          minimum: minimum.minimum, toOrder: minimum.minimum === null ? null : Math.ceil(shortfall / minimum.countUnitToStdFactor),
          toOrderUnits: minimum.minimum === null || !minimum.purchaseUnitToStdFactor ? null : Math.ceil(shortfall / minimum.purchaseUnitToStdFactor),
          value: product.estimatedValue ?? 0, hasRealMinimum: minimum.minimum !== null, supplier: minimum.supplier, otherGroupIds: groupIds.filter(id => id !== minimum.groupId),
        });
        const order = orderProducts[orderProducts.length - 1];
        if (order.toOrder !== null) product.toOrder = { qty: order.toOrder, unit: order.countUnit };
      }
    }
    groupIds.forEach(groupId => {
      const map = rows.get(groupId) ?? new Map<string, ExpectedProductRow>();
      map.set(key, { ...product, otherGroupIds: groupIds.filter(id => id !== groupId) }); rows.set(groupId, map);
    });
  }
  const groups = inputs.groups.flatMap(group => {
    const session = latest.get(group.id);
    if (!session) return [];
    const products = Array.from(rows.get(group.id)?.values() ?? []).sort((a, b) => (b.estimatedValue ?? -Infinity) - (a.estimatedValue ?? -Infinity) || a.productName.localeCompare(b.productName));
    return [{ groupKey: group.id, groupName: group.name, lastCountedAt: session.at,
      countedTotalValue: session.counts.reduce((sum, count) => sum + count.totalValue, 0), estimatedTotalValue: products.reduce((sum, product) => sum + (product.estimatedValue ?? 0), 0),
      products, baselineDates: Array.from(new Set(products.flatMap(product => product.baselineDates))).sort(),
      baselineKeys: Array.from(new Set(products.flatMap(product => product.baselineKeys))),
      belowMinProducts: orderProducts.filter(product => product.groupId === group.id || product.otherGroupIds.includes(group.id)).map(product => ({
        ...product, groupId: group.id, groupName: group.name, otherGroupIds: [product.groupId!, ...product.otherGroupIds].filter(id => id !== group.id),
      })),
    }];
  });
  return { hasData: groups.length > 0, groups, overlapSettings, countDayOrders: Array.from(selections.values()), orderProducts };
}
